/*
 File: CoursesPage.tsx
 Path: src/features/dashboard/pages/CoursesPage.tsx
 Description: Course offerings + available dates list for operator dashboard.
 Author: AQUORIX Phase 10
 Version: 1.4.0
 Created: 2026-03-23
 Last Updated: 2026-06-15
 Status: In Progress

 Change Log:
 - 2026-03-23 - Initial creation (Phase 10 UI)
 - 2026-03-23 - Fix route wiring to live backend response shape
 - 2026-03-25 - Add inline View Dates expansion for scheduled course dates
 - 2026-06-15 - v1.4.0 - Editable: + New course / Edit (offering) + Schedule date (run).
     Additive surgical edits over v1.1.0; read view, classes, GET preserved.
     Forms enforce DB CHECK rules (agency/iso_level/status enums, currency ^[A-Z]{3}$,
     price>=0, capacity>0, end>=start). Pricing in minor units. DEMO_MODE=true previews
     UX; flip to false when course write endpoints ship. Instructor + prerequisites deferred.
*/

import React, { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { apiFetch } from '../../../utils/api';
import './CoursesPage.css';

/* v1.4.0 additions — course management (additive, surgical) */
const DEMO_MODE = false; // false = real API writes; true = optimistic local preview only.

const AGENCIES = ['PADI', 'SSI', 'NAUI', 'CMAS', 'SDI', 'TDI', 'RAID', 'Other'];
const ISO_LEVELS: { v: string; label: string }[] = [
  { v: 'intro', label: 'Intro / Try Dive' },
  { v: 'iso24801-1', label: 'Level 1 - Open Water (ISO 24801-1)' },
  { v: 'iso24801-2', label: 'Level 2 - Advanced / Autonomous (ISO 24801-2)' },
  { v: 'con-ed', label: 'Continuing Ed / Specialty' },
  { v: 'iso24801-3', label: 'Level 3 - Dive Leader (ISO 24801-3)' },
];
const RUN_STATUSES = ['draft', 'published', 'cancelled'];

type CourseRun = {
  course_run_id: string;
  start_at: string;
  end_at: string | null;
  max_capacity: number;
  status: string;
  linked_session_ids: number[];
  location?: string | null;
  notes?: string | null;
  instructor_id?: string | number | null;
  instructor_name?: string | null;
  booked_count?: number | null;
};

type TeamMember = {
  leader_id: string | number;
  full_name: string;
  is_active?: boolean | null;
  is_assignable?: boolean | null;
};

type Course = {
  course_offering_id: number;
  title: string;
  certification_agency: string | null;
  course_level: string | null;
  iso_level?: string | null;
  description?: string | null;
  hero_image_url?: string | null;
  short_description?: string | null;
  base_price_minor: number | null;
  currency: string | null;
  max_students: number | null;
  duration_value?: number | null;
  duration_unit?: 'hours' | 'days' | null;
  active_flag: boolean;
  runs: CourseRun[];
};

const formatCourseDuration = (course: Course): string => {
  const value = Number(course.duration_value || 0);
  const unit = course.duration_unit;

  if (!value || !unit) return 'Not set';

  if (unit === 'hours') return value === 1 ? '1 hour' : `${value} hours`;
  if (unit === 'days') return value === 1 ? '1 day' : `${value} days`;

  return 'Not set';
};

const formatMoney = (amountMinor: number, currency: string): string => {
  return `${(Number(amountMinor) / 100).toFixed(2)} ${currency}`;
};

const formatDisplayDate = (value: string): string => {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return 'Unknown date';
  }

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const getDurationDays = (startAt: string, endAt: string): number => {
  const start = new Date(startAt);
  const end = new Date(endAt);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return 0;
  }

  const millisecondsPerDay = 1000 * 60 * 60 * 24;
  const diffInMs = end.getTime() - start.getTime();
  const diffInDays = Math.ceil(diffInMs / millisecondsPerDay);

  return Math.max(1, diffInDays);
};

const formatStatus = (status: string): string => {
  if (!status) {
    return 'Unknown';
  }

  return status
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};


const isoBadgeLabel = (value?: string | null): string => {
  if (!value) return 'ISO TBD';
  if (value === 'intro') return 'INTRO';
  if (value === 'iso24801-1') return 'ISO 24801-1';
  if (value === 'iso24801-2') return 'ISO 24801-2';
  if (value === 'con-ed') return 'CON-ED';
  if (value === 'iso24801-3') return 'ISO 24801-3';
  return value.toUpperCase();
};

const courseSection = (course: Course): 'try' | 'cert' | 'coned' => {
  if (course.iso_level === 'intro') return 'try';
  if (course.iso_level === 'iso24801-1' || course.iso_level === 'iso24801-2') return 'cert';
  return 'coned';
};

const nextFutureRun = (runs: CourseRun[]): CourseRun | null => {
  const now = Date.now();
  const futureRuns = [...(runs || [])]
    .filter((run) => new Date(run.start_at).getTime() >= now && run.status !== 'cancelled')
    .sort((a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime());
  return futureRuns[0] || null;
};


const isTomorrowDate = (date: Date): boolean => {
  const now = new Date();
  const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  return date.getFullYear() === tomorrow.getFullYear()
    && date.getMonth() === tomorrow.getMonth()
    && date.getDate() === tomorrow.getDate();
};

const formatRunDate = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Date TBD';
  if (isTomorrowDate(date)) return 'Tomorrow';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatRunTime = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Time TBD';
  return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
};

const formatRunDays = (startAt: string, endAt: string | null): string => {
  const start = new Date(startAt);
  const end = endAt ? new Date(endAt) : null;

  if (Number.isNaN(start.getTime())) return 'Time TBD';

  if (end && !Number.isNaN(end.getTime())) {
    const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    const dayMs = 1000 * 60 * 60 * 24;
    const spanDays = Math.floor((endDay.getTime() - startDay.getTime()) / dayMs) + 1;

    if (spanDays > 1) {
      return `${spanDays} days · to ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    }
  }

  if (isTomorrowDate(start)) {
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} · ${formatRunTime(startAt)}`;
  }

  return `${start.toLocaleDateString('en-US', { weekday: 'short' })} · ${formatRunTime(startAt)}`;
};

const nextRunLabel = (run: CourseRun | null): string => {
  if (!run) return 'Not scheduled';
  const date = new Date(run.start_at);
  if (Number.isNaN(date.getTime())) return 'Not scheduled';
  if (isTomorrowDate(date)) return `Tomorrow, ${formatRunTime(run.start_at)}`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const runPillClass = (status: string): string => {
  const normalized = String(status || 'scheduled').toLowerCase();
  if (['draft', 'scheduled', 'published', 'active', 'completed', 'cancelled'].includes(normalized)) {
    return `pill pill-${normalized}`;
  }
  return 'pill pill-scheduled';
};

const runCardClass = (status: string): string => {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'active' || normalized === 'published') return 'run run-active';
  if (normalized === 'cancelled') return 'run run-cancelled';
  return 'run';
};

const formatRunStatus = (status: string): string => {
  const value = String(status || 'scheduled');
  return value.charAt(0).toUpperCase() + value.slice(1);
};

const getRunBookedCount = (run: CourseRun): number => {
  if (typeof run.booked_count === 'number' && Number.isFinite(run.booked_count)) return run.booked_count;
  return 0;
};

const capacityPercent = (run: CourseRun): number => {
  const max = Number(run.max_capacity || 0);
  if (!max) return 0;
  return Math.min(100, Math.max(0, Math.round((getRunBookedCount(run) / max) * 100)));
};

const sortedCourseRuns = (runs: CourseRun[]): CourseRun[] => {
  return [...(runs || [])].sort((a, b) => new Date(a.start_at).getTime() - new Date(b.start_at).getTime());
};

const priceParts = (amountMinor: number | null, currency: string | null) => {
  if (amountMinor === null || amountMinor === undefined) return { amount: 'TBD', currency: currency || '' };
  return { amount: (Number(amountMinor) / 100).toFixed(2), currency: currency || '' };
};

function CourseOfferingCard({
  course,
  open,
  onToggle,
  onSchedule,
  onEditCourse,
  onEditRun,
  onCancelRun,
  onDeleteRun,
  onDeleteCourse,
}: {
  course: Course;
  open: boolean;
  onToggle: () => void;
  onSchedule: () => void;
  onEditCourse: () => void;
  onEditRun: (run: CourseRun) => void;
  onCancelRun: (run: CourseRun) => void;
  onDeleteRun: (run: CourseRun) => void;
  onDeleteCourse: () => void;
}) {
  const next = nextFutureRun(course.runs || []);
  const upcomingCount = (course.runs || []).filter((run) => new Date(run.start_at).getTime() >= Date.now() && run.status !== 'cancelled').length;
  const price = priceParts(course.base_price_minor, course.currency);

  return (
    <article className={open ? "offering open" : "offering"}>
      <div className="off-main" onClick={onToggle}>
        <div className="off-left">
          <div className="off-title-row">
            <h3 className={course.active_flag === false ? "off-title muted" : "off-title"}>{course.title}</h3>
            <div className="badges">
              {course.certification_agency ? <span className="badge badge-agency">{course.certification_agency}</span> : null}
              <span className="badge badge-iso">{isoBadgeLabel(course.iso_level)}</span>
              {course.active_flag === false ? <span className="badge badge-archived">ARCHIVED</span> : null}
            </div>
          </div>
          <p className="off-desc">{course.description || course.course_level || "Course description not set yet."}</p>
          <div className="off-stats">
            <div className="stat"><span className="k">Next date</span><span className={next ? "v next" : "v none"}>{nextRunLabel(next)}</span></div>
            <div className="stat"><span className="k">Upcoming runs</span><span className="v">{upcomingCount}</span></div>
            <div className="stat"><span className="k">Max students</span><span className="v">{course.max_students || "TBD"}</span></div>
            <div className="stat"><span className="k">Duration</span><span className={course.duration_value && course.duration_unit ? "v" : "v none"}>{formatCourseDuration(course)}</span></div>
          </div>
        </div>
        <div className="off-right">
          <div className="price"><span className="from">from</span>{price.amount}<span className="cur">{price.currency}</span></div>
          <button className="btn-edit-course" type="button" onClick={(event) => { event.stopPropagation(); onEditCourse(); }}>Edit course</button>
          <button className="icon-btn danger" type="button" title="Delete course" onClick={(event) => { event.stopPropagation(); onDeleteCourse(); }}><svg viewBox="0 0 24 24"><path d="M3 6h18M8 6V4h8v2M9 10v8M15 10v8M6 6l1 14h10l1-14"/></svg></button>
          <div className="caret"><svg viewBox="0 0 24 24"><path d="M6 9l6 6 6-6"/></svg></div>
        </div>
      </div>
      <div className="runs">
        <div className="runs-head">
          <h4>Scheduled runs</h4>
          <button
            className="btn-schedule"
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onSchedule();
            }}
          >
            <svg viewBox="0 0 24 24"><path d="M12 5v14M5 12h14"/></svg> Schedule a date
          </button>
        </div>
        {sortedCourseRuns(course.runs || []).length === 0 ? (
          <div className="runs-empty">No dates scheduled yet. <b>Schedule a date</b> to make this course bookable on your widget and app.</div>
        ) : (
          sortedCourseRuns(course.runs || []).map((run) => {
            const booked = getRunBookedCount(run);
            const capacity = Number(run.max_capacity || 0);
            const pct = capacityPercent(run);
            const instructorLabel = run.instructor_name || (run.instructor_id ? `Instructor #${run.instructor_id}` : 'Instructor TBD');

            return (
              <div key={run.course_run_id} className={runCardClass(run.status)}>
                <div className="run-when">
                  <div className="run-date">{formatRunDate(run.start_at)}</div>
                  <div className="run-days">{formatRunDays(run.start_at, run.end_at)}</div>
                </div>
                <div className="run-mid">
                  <span className={run.instructor_name || run.instructor_id ? "instr" : "instr tbd"}>{instructorLabel}</span>
                  {' · '}
                  <span className="loc">{run.location || 'Location TBD'}</span>
                  {Array.isArray(run.linked_session_ids) && run.linked_session_ids.length > 0 ? <> · linked dives {run.linked_session_ids.length}</> : null}
                </div>
                <div className="run-cap">
                  <div className="cap-top"><span className="used">{booked} booked</span><span className="max">/ {capacity || 'TBD'}</span></div>
                  <div className={capacity > 0 && booked >= capacity ? "cap-bar cap-full" : "cap-bar"}><div className="cap-fill" style={{ width: `${pct}%` }}></div></div>
                </div>
                <div className="run-status"><span className={runPillClass(run.status)}>{formatRunStatus(run.status)}</span></div>
                <div className="run-acts">
                  <button className="icon-btn" type="button" title="Edit run" onClick={() => onEditRun(run)}><svg viewBox="0 0 24 24"><path d="M4 20h4L18 10l-4-4L4 16v4zM14 6l4 4"/></svg></button>
                  <button className="icon-btn" type="button" title="Cancel run" onClick={() => onCancelRun(run)}><svg viewBox="0 0 24 24"><path d="M7 7l10 10M17 7L7 17"/></svg></button>
                  <button className="icon-btn danger" type="button" title="Delete run" onClick={() => onDeleteRun(run)}><svg viewBox="0 0 24 24"><path d="M3 6h18M8 6V4h8v2M9 10v8M15 10v8M6 6l1 14h10l1-14"/></svg></button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </article>
  );
}

/* v1.4.0 — modal styles + forms (additive) */
const ui = {
  scrim: { position: 'fixed', inset: 0, background: 'rgba(20,36,46,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60, padding: 20 } as React.CSSProperties,
  modal: { background: 'var(--surface)', borderRadius: 14, width: 520, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(20,36,46,.3)' } as React.CSSProperties,
  head: { padding: '18px 22px 4px' } as React.CSSProperties,
  body: { padding: '8px 22px 18px' } as React.CSSProperties,
  foot: { display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '14px 22px', borderTop: '1px solid var(--line)' } as React.CSSProperties,
  label: { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--aqx-slate,#4a5c6a)', margin: '12px 0 4px' } as React.CSSProperties,
  input: { width: '100%', boxSizing: 'border-box', border: '1px solid var(--topnav-border,#e0e0e0)', borderRadius: 8, padding: '9px 11px', fontSize: 14, background: 'var(--surface)' } as React.CSSProperties,
  row2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 } as React.CSSProperties,
  btnPrimary: { background: 'var(--aqx-primary,#1B4D6F)', color: 'var(--surface)', border: 'none', borderRadius: 8, padding: '10px 16px', fontSize: 14, fontWeight: 700, cursor: 'pointer' } as React.CSSProperties,
  btnGhost: { background: 'var(--surface)', border: '1px solid var(--topnav-border,#e0e0e0)', borderRadius: 8, padding: '10px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer' } as React.CSSProperties,
  err: { color: 'var(--full)', fontSize: 12.5, marginTop: 8 } as React.CSSProperties,
};

type OfferingDraft = {
  title: string;
  certification_agency: string;
  course_level: string;
  iso_level: string;
  description: string;
  short_description: string;
  hero_image_url: string;
  price: string;
  currency: string;
  max_students: string;
  duration_value: string;
  duration_unit: 'hours' | 'days';
};

function OfferingModal({ initial, apiBase, onClose, onSave }: { initial?: Course; apiBase: string | null; onClose: () => void; onSave: (d: OfferingDraft) => void }) {
  const [d, setD] = useState<OfferingDraft>({
    title: initial?.title ?? '',
    certification_agency: initial?.certification_agency ?? 'PADI',
    course_level: initial?.course_level ?? '',
    iso_level: initial?.iso_level ?? 'intro',
    description: initial?.description ?? '',
    short_description: initial?.short_description ?? '',
    hero_image_url: initial?.hero_image_url ?? '',
    price: initial && initial.base_price_minor !== null && initial.base_price_minor !== undefined ? (initial.base_price_minor / 100).toFixed(2) : '',
    currency: initial?.currency ?? 'JOD',
    max_students: initial?.max_students ? String(initial.max_students) : '',
    duration_value: initial?.duration_value ? String(initial.duration_value) : '',
    duration_unit: initial?.duration_unit === 'days' ? 'days' : 'hours',
  });
  const [err, setErr] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageError, setImageError] = useState('');

  const set = (k: keyof OfferingDraft, v: string) => setD((p) => ({ ...p, [k]: v }));

  const uploadHeroImage = async (file: File | null) => {
    if (!file || !initial || !apiBase) return;

    setUploadingImage(true);
    setImageError('');

    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await apiFetch(`${apiBase}/${initial.course_offering_id}/hero-image`, {
        method: 'POST',
        body: formData,
      });
      const payload = await response.json().catch(() => ({}));

      if (!response.ok || payload.ok === false || !payload.hero_image_url) {
        setImageError(payload.error || payload.message || 'Image could not be uploaded.');
        return;
      }

      set('hero_image_url', payload.hero_image_url);
    } catch (error) {
      console.error('Course hero image upload failed', error);
      setImageError('Image could not be uploaded.');
    } finally {
      setUploadingImage(false);
    }
  };

  const submit = () => {
    if (!d.title.trim()) return setErr('Course title is required.');
    if (d.currency && !/^[A-Z]{3}$/.test(d.currency)) return setErr('Currency must be 3 uppercase letters.');
    if (d.price && Number(d.price) < 0) return setErr('Price per student cannot be negative.');
    if (d.max_students && Number(d.max_students) <= 0) return setErr('Max students per run must be greater than 0.');
    if (d.duration_value && (!Number.isInteger(Number(d.duration_value)) || Number(d.duration_value) <= 0)) return setErr('Duration must be a positive whole number.');
    setErr('');
    onSave(d);
  };

  return (
    <div className="scrim show" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head"><span className="tag"></span><h3>{initial ? 'Edit course' : 'New course'}</h3></div>

        <div className="modal-body">
          <label className="mlabel">Course title *</label>
          <input className="minput" value={d.title} onChange={(e) => set('title', e.target.value)} placeholder="e.g. Open Water Scuba Diver" />

          <div className="row2">
            <div>
              <label className="mlabel">Certifying agency</label>
              <select className="mselect" value={d.certification_agency} onChange={(e) => set('certification_agency', e.target.value)}>
                <option value="PADI">PADI</option>
                <option value="SSI">SSI</option>
                <option value="NAUI">NAUI</option>
                <option value="CMAS">CMAS</option>
                <option value="SDI">SDI</option>
                <option value="TDI">TDI</option>
                <option value="RAID">RAID</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label className="mlabel">ISO level</label>
              <select className="mselect" value={d.iso_level} onChange={(e) => set('iso_level', e.target.value)}>
                <option value="intro">Intro / try dive</option>
                <option value="iso24801-1">Supervised diver (ISO 24801-1)</option>
                <option value="iso24801-2">Autonomous diver (ISO 24801-2)</option>
                <option value="con-ed">Continuing education</option>
                <option value="iso24801-3">Dive leader (ISO 24801-3)</option>
              </select>
            </div>
          </div>

          <label className="mlabel">Agency level label</label>
          <input className="minput" value={d.course_level} onChange={(e) => set('course_level', e.target.value)} placeholder="e.g. Level 1, Specialty, Advanced" />
          <p className="hint">The ISO level is the cross-agency truth AQUORIX reads. The label is what you print on the card.</p>

          <label className="mlabel">Description</label>
          <input className="minput" value={d.description} onChange={(e) => set('description', e.target.value)} placeholder="One line divers will see on your widget" />

          <div className="experience-image-block">
            <div className="experience-image-preview">
              {d.hero_image_url ? (
                <img src={d.hero_image_url} alt="" />
              ) : (
                <div className="experience-image-empty">No course image yet</div>
              )}
            </div>
            <div className="experience-image-actions">
              <label className={initial ? "experience-upload-btn" : "experience-upload-btn disabled"}>
                {uploadingImage ? 'Uploading...' : d.hero_image_url ? 'Replace image' : 'Upload image'}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  disabled={!initial || uploadingImage}
                  onChange={(event) => uploadHeroImage(event.target.files?.[0] || null)}
                />
              </label>
              <p className="hint">{initial ? 'Landscape image for concierge kiosk, widget, and customer-facing course cards.' : 'Create the course first, then edit it to upload an image.'}</p>
              {imageError ? <p className="field-error">{imageError}</p> : null}
            </div>
          </div>

          <label className="mlabel">Kiosk short description</label>
          <textarea className="minput mtextarea" value={d.short_description} onChange={(e) => set('short_description', e.target.value)} placeholder="Two or three lines for concierge kiosks and customer-facing course cards." />

          <div className="row2">
            <div>
              <label className="mlabel">Price per student</label>
              <input className="minput" value={d.price} onChange={(e) => set('price', e.target.value)} placeholder="450.00" />
            </div>
            <div>
              <label className="mlabel">Currency</label>
              <input className="minput" value={d.currency} onChange={(e) => set('currency', e.target.value.toUpperCase())} />
            </div>
          </div>

          <div className="row2">
            <div>
              <label className="mlabel">Max students per run</label>
              <input className="minput" value={d.max_students} onChange={(e) => set('max_students', e.target.value)} placeholder="8" />
            </div>
            <div>
              <label className="mlabel">Duration</label>
              <div className="row2 compact">
                <input className="minput" value={d.duration_value} onChange={(e) => set('duration_value', e.target.value)} placeholder="3" />
                <select className="mselect" value={d.duration_unit} onChange={(e) => set('duration_unit', e.target.value as 'hours' | 'days')}>
                  <option value="hours">Hours</option>
                  <option value="days">Days</option>
                </select>
              </div>
            </div>
          </div>

          {err && <p className="hint">{err}</p>}
        </div>

        <div className="modal-foot">
          <button className="mbtn-ghost" type="button" onClick={onClose}>Cancel</button>
          <button className="mbtn-primary" type="button" onClick={submit}>{initial ? 'Save changes' : 'Create course'}</button>
        </div>
      </div>
    </div>
  );
}

type RunDraft = {
  start_date: string;
  start_time: string;
  end_date: string;
  max_capacity: string;
  status: string;
  instructor_id: string;
  location: string;
};

function ymdFromIso(value?: string | null): string {
  if (!value) return '';
  return String(value).slice(0, 10);
}

function timeFromIso(value?: string | null): string {
  if (!value) return '09:00';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '09:00';
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

function addCalendarDays(ymd: string, daysToAdd: number): string {
  if (!ymd) return '';
  const [year, month, day] = ymd.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  if (Number.isNaN(date.getTime())) return '';
  date.setDate(date.getDate() + daysToAdd);
  const nextYear = date.getFullYear();
  const nextMonth = String(date.getMonth() + 1).padStart(2, '0');
  const nextDay = String(date.getDate()).padStart(2, '0');
  return `${nextYear}-${nextMonth}-${nextDay}`;
}

function derivedRunEndDate(course: Course, startDate: string): string {
  const value = Number(course.duration_value || 0);
  const unit = course.duration_unit;

  if (!startDate || !value || !unit) return '';

  if (unit === 'days') return addCalendarDays(startDate, Math.max(value - 1, 0));

  return startDate;
}

type CourseTimeOption = { value: string; label: string };

const COURSE_TIME_OPTIONS: CourseTimeOption[] = Array.from({ length: ((21 - 5) * 4) + 1 }, (_, index) => {
  const totalMinutes = (5 * 60) + (index * 15);
  const hour24 = Math.floor(totalMinutes / 60);
  const minute = totalMinutes % 60;
  const value = `${String(hour24).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  const hour12 = hour24 % 12 || 12;
  const suffix = hour24 < 12 ? 'AM' : 'PM';
  return { value, label: `${hour12}:${String(minute).padStart(2, '0')} ${suffix}` };
});

function timeOptionLabel(value: string): string {
  const [hourText, minuteText] = value.split(':');
  const hour24 = Number(hourText);
  const minute = Number(minuteText);
  if (!Number.isFinite(hour24) || !Number.isFinite(minute)) return value;
  const hour12 = hour24 % 12 || 12;
  const suffix = hour24 < 12 ? 'AM' : 'PM';
  return `${hour12}:${String(minute).padStart(2, '0')} ${suffix}`;
}

function timeSelectOptions(currentValue: string): CourseTimeOption[] {
  if (!currentValue || COURSE_TIME_OPTIONS.some((option) => option.value === currentValue)) {
    return COURSE_TIME_OPTIONS;
  }
  return [{ value: currentValue, label: `${timeOptionLabel(currentValue)} (current)` }, ...COURSE_TIME_OPTIONS];
}

function RunModal({ course, initial, team, onClose, onSave }: { course: Course; initial?: CourseRun; team: TeamMember[]; onClose: () => void; onSave: (d: RunDraft) => void }) {
  const initialStartDate = ymdFromIso(initial?.start_at);
  const durationDerived = Boolean(course.duration_value && course.duration_unit);
  const initialDerivedEndDate = durationDerived ? derivedRunEndDate(course, initialStartDate) : '';

  const [d, setD] = useState<RunDraft>({
    start_date: initialStartDate,
    start_time: timeFromIso(initial?.start_at),
    end_date: initialDerivedEndDate || ymdFromIso(initial?.end_at),
    max_capacity: String(initial?.max_capacity || course.max_students || 8),
    status: initial?.status || 'draft',
    instructor_id: initial?.instructor_id ? String(initial.instructor_id) : '',
    location: initial?.location || '',
  });
  const [err, setErr] = useState('');

  const set = (k: keyof RunDraft, v: string) => setD((p) => ({ ...p, [k]: v }));
  const setStartDate = (value: string) => setD((p) => ({ ...p, start_date: value, end_date: durationDerived ? derivedRunEndDate(course, value) : p.end_date }));
  const setEndDate = (value: string) => setD((p) => ({ ...p, end_date: value }));

  const submit = () => {
    if (!d.start_date) return setErr('Start date is required.');
    if (!d.start_time || !/^\d{2}:\d{2}$/.test(d.start_time)) return setErr('Start time is required.');
    if (!d.max_capacity || Number(d.max_capacity) <= 0) return setErr('Max capacity must be greater than 0.');
    if (d.end_date && d.end_date < d.start_date) return setErr('End date must be on or after the start date.');
    setErr('');
    onSave(d);
  };

  return (
    <div className="scrim show" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head"><span className="tag"></span><h3>{initial ? 'Edit date' : 'Schedule a date'}</h3></div>
        <div className="modal-body">
          <div className="row2">
            <div>
              <label className="mlabel">Start date *</label>
              <input className="minput" type="date" value={d.start_date} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div>
              <label className="mlabel">Start time *</label>
              <select className="mselect" value={d.start_time} onChange={(e) => set('start_time', e.target.value)}>
                {timeSelectOptions(d.start_time).map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="row2">
            <div>
              <label className="mlabel">End date</label>
              <input className="minput" type="date" value={d.end_date} readOnly={durationDerived} onChange={(e) => setEndDate(e.target.value)} />
            </div>
            <div>
              <label className="mlabel">Max capacity *</label>
              <input className="minput" value={d.max_capacity} onChange={(e) => set('max_capacity', e.target.value)} placeholder="8" />
            </div>
          </div>

          <label className="mlabel">Status</label>
          <select className="mselect" value={d.status} onChange={(e) => set('status', e.target.value)}>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>

          <label className="mlabel">Instructor</label>
          <select className="mselect" value={d.instructor_id} onChange={(e) => set('instructor_id', e.target.value)}>
            <option value="">Assign later (TBD)</option>
            {team
              .filter((member) => member.is_active !== false && member.is_assignable !== false)
              .map((member) => (
                <option key={String(member.leader_id)} value={String(member.leader_id)}>
                  {member.full_name || `Leader #${member.leader_id}`}
                </option>
              ))}
          </select>

          <label className="mlabel">Location</label>
          <input className="minput" value={d.location} onChange={(e) => set('location', e.target.value)} placeholder="e.g. Boat + shore circuit" />

          <p className="hint">{durationDerived ? `End date is derived from course duration: ${formatCourseDuration(course)}.` : 'Draft dates stay on this Courses page. Published dates appear on the schedule, ops board, widget, and app.'}</p>

          {err && <p className="hint">{err}</p>}
        </div>

        <div className="modal-foot">
          <button className="mbtn-ghost" type="button" onClick={onClose}>Cancel</button>
          <button className="mbtn-primary" type="button" onClick={submit}>{initial ? 'Save changes' : 'Schedule run'}</button>
        </div>
      </div>
    </div>
  );
}

type CoursesOutletContext = {
  me?: {
    operator_context?: {
      active_operator_id?: string | number | null;
    };
  } | null;
};

const CoursesPage: React.FC = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedCourseId, setExpandedCourseId] = useState<number | null>(null);
  const [offeringModal, setOfferingModal] = useState<{ course?: Course } | null>(null);
  const [runModal, setRunModal] = useState<{ course: Course; run?: CourseRun } | null>(null);
  const [pendingRunAction, setPendingRunAction] = useState<{ action: 'cancel' | 'delete'; course: Course; run: CourseRun } | null>(null);
  const [runActionSubmitting, setRunActionSubmitting] = useState(false);
  const [runActionError, setRunActionError] = useState<string | null>(null);
  const [pendingCourseDelete, setPendingCourseDelete] = useState<Course | null>(null);
  const [courseDeleteSubmitting, setCourseDeleteSubmitting] = useState(false);
  const [courseDeleteError, setCourseDeleteError] = useState<string | null>(null);

  const { me } = useOutletContext<CoursesOutletContext>();
  const activeOperatorId = me?.operator_context?.active_operator_id || null;
  const operatorId = activeOperatorId ? Number(activeOperatorId) : null;

  const apiBase = operatorId ? `/api/v3/operator/${operatorId}/courses` : null;

  const fetchCourses = async () => {
    if (!apiBase) {
      setCourses([]);
      setLoading(false);
      return;
    }

    try {
      const res = await apiFetch(apiBase);
      const data = await res.json();
      setCourses(data.courses || []);
    } catch (err) {
      console.error('Failed to load courses', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeam = async () => {
    try {
      const res = await apiFetch('/api/v1/dashboard/team');
      const data = await res.json();
      setTeam(Array.isArray(data.team) ? data.team : []);
    } catch (err) {
      console.error('Failed to load team', err);
      setTeam([]);
    }
  };

  useEffect(() => {
    fetchCourses();
    fetchTeam();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operatorId]);

  const saveOffering = async (d: OfferingDraft) => {
    if (!apiBase) return;

    const body = {
      title: d.title.trim(),
      certification_agency: d.certification_agency || null,
      course_level: d.course_level || null,
      iso_level: d.iso_level || null,
      description: d.description || null,
      short_description: d.short_description || null,
      hero_image_url: d.hero_image_url || null,
      base_price_minor: d.price ? Math.round(Number(d.price) * 100) : null,
      currency: d.currency || null,
      max_students: d.max_students ? Number(d.max_students) : null,
      duration_value: d.duration_value ? Number(d.duration_value) : null,
      duration_unit: d.duration_value ? d.duration_unit : null,
    };
    const editing = offeringModal?.course;
    if (DEMO_MODE) {
      if (editing) {
        setCourses((cs) => cs.map((c) => c.course_offering_id === editing.course_offering_id
          ? ({ ...c, ...body, base_price_minor: body.base_price_minor ?? 0, max_students: body.max_students ?? 0 } as Course) : c));
      } else {
        setCourses((cs) => [...cs, ({
          course_offering_id: Date.now(), active_flag: true, runs: [], ...body,
          base_price_minor: body.base_price_minor ?? 0, max_students: body.max_students ?? 0,
          certification_agency: body.certification_agency || '', course_level: body.course_level || '',
          currency: body.currency || 'JOD',
        } as Course)]);
      }
    } else {
      const url = editing ? `${apiBase}/${editing.course_offering_id}` : apiBase;
      await apiFetch(url, { method: editing ? 'PATCH' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      await fetchCourses();
    }
    setOfferingModal(null);
  };

  const saveRun = async (course: Course, d: RunDraft, run?: CourseRun) => {
    if (!apiBase) return;

    const runTime = d.start_time || '09:00';
    const start_at = new Date(`${d.start_date}T${runTime}`).toISOString();
    const end_at = d.end_date ? new Date(`${d.end_date}T${runTime}`).toISOString() : null;
    const body = { start_at, end_at, max_capacity: Number(d.max_capacity), status: d.status, location: d.location || null, instructor_id: d.instructor_id ? Number(d.instructor_id) : null };
    if (DEMO_MODE) {
      setCourses((cs) => cs.map((c) => {
        if (c.course_offering_id !== course.course_offering_id) return c;
        if (run) {
          return { ...c, runs: c.runs.map((r) => r.course_run_id === run.course_run_id ? ({ ...r, ...body, end_at: end_at || start_at } as CourseRun) : r) };
        }
        return { ...c, runs: [...c.runs, ({ course_run_id: `tmp-${Date.now()}`, linked_session_ids: [], ...body, end_at: end_at || start_at } as CourseRun)] };
      }));
      setExpandedCourseId(course.course_offering_id);
    } else {
      if (run) {
        await apiFetch(`${apiBase}/runs/${run.course_run_id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      } else {
        await apiFetch(`${apiBase}/${course.course_offering_id}/runs`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      }
      await fetchCourses();
    }
    setRunModal(null);
  };

  const cancelRun = (course: Course, run: CourseRun) => {
    if (!apiBase) return;

    setRunActionError(null);
    setPendingRunAction({ action: 'cancel', course, run });
  };

  const deleteRun = (course: Course, run: CourseRun) => {
    if (!apiBase) return;

    setRunActionError(null);
    setPendingRunAction({ action: 'delete', course, run });
  };

  const confirmRunAction = async () => {
    if (!apiBase || !pendingRunAction) return;

    const { action, course, run } = pendingRunAction;
    setRunActionSubmitting(true);
    setRunActionError(null);

    try {
      if (action === 'cancel') {
        if (DEMO_MODE) {
          setCourses((cs) => cs.map((c) => c.course_offering_id === course.course_offering_id
            ? ({ ...c, runs: c.runs.map((r) => r.course_run_id === run.course_run_id ? ({ ...r, status: 'cancelled' } as CourseRun) : r) })
            : c));
        } else {
          const res = await apiFetch(`${apiBase}/runs/${run.course_run_id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ status: 'cancelled' }),
          });

          if (!res.ok) {
            const data = await res.json().catch(() => null);
            setRunActionError(data?.error || 'Failed to cancel course run');
            return;
          }

          await fetchCourses();
        }
      } else {
        if (DEMO_MODE) {
          setCourses((cs) => cs.map((c) => c.course_offering_id === course.course_offering_id
            ? ({ ...c, runs: c.runs.filter((r) => r.course_run_id !== run.course_run_id) })
            : c));
        } else {
          const res = await apiFetch(`${apiBase}/runs/${run.course_run_id}`, {
            method: 'DELETE',
          });

          if (!res.ok) {
            const data = await res.json().catch(() => null);
            setRunActionError(data?.error || 'Failed to delete course run');
            return;
          }

          await fetchCourses();
        }
      }

      setPendingRunAction(null);
    } finally {
      setRunActionSubmitting(false);
    }
  };


  const deleteCourse = (course: Course) => {
    if (!apiBase) return;

    setCourseDeleteError(null);
    setPendingCourseDelete(course);
  };

  const confirmCourseDelete = async () => {
    if (!apiBase || !pendingCourseDelete) return;

    setCourseDeleteSubmitting(true);
    setCourseDeleteError(null);

    try {
      const res = await apiFetch(`${apiBase}/${pendingCourseDelete.course_offering_id}`, {
        method: 'DELETE',
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        setCourseDeleteError(data?.error || 'Failed to delete course');
        return;
      }

      setPendingCourseDelete(null);
      await fetchCourses();
    } finally {
      setCourseDeleteSubmitting(false);
    }
  };

  const sortedCourses = useMemo(() => {
    return [...courses].sort((a, b) => a.title.localeCompare(b.title));
  }, [courses]);

  const toggleDates = (courseOfferingId: number) => {
    setExpandedCourseId((prev) =>
      prev === courseOfferingId ? null : courseOfferingId
    );
  };

  return (
    <div className="courses-v2">
      {pendingCourseDelete ? (
        <div className="scrim show" role="dialog" aria-modal="true" aria-labelledby="course-delete-title">
          <div className="modal">
            <div className="modal-head">
              <span className="tag"></span>
              <h3 id="course-delete-title">Delete course</h3>
            </div>
            <div className="modal-body">
              <p className="hint">This permanently removes the course offering. Scheduled runs must be deleted first.</p>
              <p className="hint"><b>{pendingCourseDelete.title}</b></p>
              {courseDeleteError ? <p className="hint">{courseDeleteError}</p> : null}
            </div>
            <div className="modal-foot">
              <button className="mbtn-ghost" type="button" disabled={courseDeleteSubmitting} onClick={() => setPendingCourseDelete(null)}>Keep course</button>
              <button className="mbtn-primary" type="button" disabled={courseDeleteSubmitting} onClick={confirmCourseDelete}>{courseDeleteSubmitting ? 'Working...' : 'Delete course'}</button>
            </div>
          </div>
        </div>
      ) : null}

      {pendingRunAction ? (
        <div className="scrim show" role="dialog" aria-modal="true" aria-labelledby="run-action-title">
          <div className="modal">
            <div className="modal-head">
              <span className="tag"></span>
              <h3 id="run-action-title">{pendingRunAction.action === 'delete' ? 'Delete course run' : 'Cancel course run'}</h3>
            </div>
            <div className="modal-body">
              <p className="hint">
                {pendingRunAction.action === 'delete'
                  ? 'This permanently removes the scheduled run if it has no bookings.'
                  : 'This keeps the run on the course and changes its status to cancelled.'}
              </p>
              <p className="hint"><b>{formatRunDate(pendingRunAction.run.start_at)}</b> · {formatRunDays(pendingRunAction.run.start_at, pendingRunAction.run.end_at)}</p>
              {runActionError ? <p className="hint">{runActionError}</p> : null}
            </div>
            <div className="modal-foot">
              <button className="mbtn-ghost" type="button" disabled={runActionSubmitting} onClick={() => setPendingRunAction(null)}>Keep run</button>
              <button className="mbtn-primary" type="button" disabled={runActionSubmitting} onClick={confirmRunAction}>{runActionSubmitting ? 'Working...' : pendingRunAction.action === 'delete' ? 'Delete run' : 'Cancel run'}</button>
            </div>
          </div>
        </div>
      ) : null}
      <header className="crown">
        <div className="crown-row">
          <div>
            <h1>Courses</h1>
            <p className="sub">Your teaching catalog and the dates you are running it. A course is what you offer; a date is a run divers can book — on your board, your widget, and the app.</p>
          </div>
          <div className="crown-note">Courses you publish here appear on <b>your widget</b> and <b>the app</b>, bookable alongside dives.</div>
        </div>
      </header>

      <div className="toolbar">
        <div className="filters">
          <button className="chip on">All <span className="n">{sortedCourses.length}</span></button>
          <button className="chip">Try dives <span className="n">{sortedCourses.filter((course) => courseSection(course) === 'try').length}</span></button>
          <button className="chip">Certifications <span className="n">{sortedCourses.filter((course) => courseSection(course) === 'cert').length}</span></button>
          <button className="chip">Continuing ed <span className="n">{sortedCourses.filter((course) => courseSection(course) === 'coned').length}</span></button>
          <button className="chip">Archived</button>
        </div>
        <div className="course-actions">
          <div className="search">
            <svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4-4"/></svg>
            <input placeholder="Search courses" readOnly />
          </div>
          <button className="btn-new" type="button" onClick={() => setOfferingModal({})}>
            <svg viewBox="0 0 24 24"><path d="M12 5v14M5 12h14"/></svg> New course
          </button>
        </div>
      </div>

      <div className="col">
        {loading ? <div className="runs-empty">Loading courses...</div> : null}

        {!loading && sortedCourses.length === 0 ? (
          <div className="runs-empty">No courses found for this operator.</div>
        ) : null}

        {!loading && sortedCourses.filter((course) => courseSection(course) === 'try').length > 0 ? (
          <>
            <div className="seclabel"><h2>Try dives &amp; intros</h2><div className="ln"></div></div>
            {sortedCourses.filter((course) => courseSection(course) === 'try').map((course) => (
              <CourseOfferingCard
                key={course.course_offering_id}
                course={course}
                open={expandedCourseId === course.course_offering_id}
                onToggle={() => toggleDates(course.course_offering_id)}
                onSchedule={() => setRunModal({ course })}
                onEditCourse={() => setOfferingModal({ course })}
                onEditRun={(run) => setRunModal({ course, run })}
                onCancelRun={(run) => cancelRun(course, run)}
                onDeleteRun={(run) => deleteRun(course, run)}
                onDeleteCourse={() => deleteCourse(course)}
              />
            ))}
          </>
        ) : null}

        {!loading && sortedCourses.filter((course) => courseSection(course) === 'cert').length > 0 ? (
          <>
            <div className="seclabel"><h2>Certification courses</h2><div className="ln"></div></div>
            {sortedCourses.filter((course) => courseSection(course) === 'cert').map((course) => (
              <CourseOfferingCard
                key={course.course_offering_id}
                course={course}
                open={expandedCourseId === course.course_offering_id}
                onToggle={() => toggleDates(course.course_offering_id)}
                onSchedule={() => setRunModal({ course })}
                onEditCourse={() => setOfferingModal({ course })}
                onEditRun={(run) => setRunModal({ course, run })}
                onCancelRun={(run) => cancelRun(course, run)}
                onDeleteRun={(run) => deleteRun(course, run)}
                onDeleteCourse={() => deleteCourse(course)}
              />
            ))}
          </>
        ) : null}

        {!loading && sortedCourses.filter((course) => courseSection(course) === 'coned').length > 0 ? (
          <>
            <div className="seclabel"><h2>Continuing education &amp; specialties</h2><div className="ln"></div></div>
            {sortedCourses.filter((course) => courseSection(course) === 'coned').map((course) => (
              <CourseOfferingCard
                key={course.course_offering_id}
                course={course}
                open={expandedCourseId === course.course_offering_id}
                onToggle={() => toggleDates(course.course_offering_id)}
                onSchedule={() => setRunModal({ course })}
                onEditCourse={() => setOfferingModal({ course })}
                onEditRun={(run) => setRunModal({ course, run })}
                onCancelRun={(run) => cancelRun(course, run)}
                onDeleteRun={(run) => deleteRun(course, run)}
                onDeleteCourse={() => deleteCourse(course)}
              />
            ))}
          </>
        ) : null}

        <div className="legend">
          <span className="sw"><span className="dot course-dot"></span> Course offering</span>
          <span className="sw"><span className="dot published-dot"></span> Published — live on schedule, ops board, widget, and app</span>
          <span className="sw"><span className="dot draft-dot"></span> Draft — Courses page only</span>
          <span className="sw legend-note"><b>Price is gold. Prerequisites are guidance, never a barrier.</b></span>
        </div>
      </div>

      {offeringModal && (
        <OfferingModal
          initial={offeringModal.course}
          apiBase={apiBase}
          onClose={() => setOfferingModal(null)}
          onSave={saveOffering}
        />
      )}

      {runModal && (
        <RunModal
          course={runModal.course}
          initial={runModal.run}
          team={team}
          onClose={() => setRunModal(null)}
          onSave={(d) => saveRun(runModal.course, d, runModal.run)}
        />
      )}
    </div>
  );
};

export default CoursesPage;
